// src/components/Sidebar.jsx
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useWorkspaces } from "../hooks/useFirestore";
import toast from "react-hot-toast";
import "../styles/sidebar.css";

export default function Sidebar({ activeWorkspace, onSelectWorkspace }) {
  const { user, logout } = useAuth();
  const { workspaces, loading, createWorkspace, deleteWorkspace } = useWorkspaces();
  const [showNewWs, setShowNewWs] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [creating, setCreating] = useState(false);
  const [hoveredWs, setHoveredWs] = useState(null);

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWsName.trim()) return;
    setCreating(true);
    try {
      const id = await createWorkspace(newWsName.trim());
      setNewWsName("");
      setShowNewWs(false);
      onSelectWorkspace(id);
      toast.success("Đã tạo workspace!");
    } catch {
      toast.error("Tạo thất bại, thử lại nhé.");
    }
    setCreating(false);
  };

  const handleDeleteWorkspace = async (e, ws) => {
    e.stopPropagation(); // không kích hoạt onClick của ws-item
    if (ws.ownerId !== user.uid) {
      toast.error("Chỉ người tạo mới được xóa workspace.");
      return;
    }
    if (!window.confirm(`Xóa workspace "${ws.name}"?\nTất cả tasks bên trong cũng sẽ bị xóa.`)) return;
    try {
      await deleteWorkspace(ws.id);
      // Nếu đang xem workspace bị xóa → reset
      if (activeWorkspace === ws.id) onSelectWorkspace(null);
      toast.success("Đã xóa workspace.");
    } catch {
      toast.error("Xóa thất bại, thử lại nhé.");
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Đã đăng xuất");
  };

  return (
    <aside className="sidebar">
      {/* Brand */}
      <div className="sidebar-brand">
        <div className="brand-icon">
          <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
            <path d="M8 11h10M8 16h16M8 21h7" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="24" cy="11" r="3.5" fill="currentColor"/>
          </svg>
        </div>
        <span className="brand-text">TeamTodo</span>
      </div>

      {/* Workspaces */}
      <div className="sidebar-section">
        <div className="section-header">
          <span className="section-label">Workspace</span>
          <button
            className="icon-btn"
            onClick={() => setShowNewWs((v) => !v)}
            title="Tạo workspace mới"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
        </div>

        {showNewWs && (
          <form className="new-ws-form" onSubmit={handleCreateWorkspace}>
            <input
              autoFocus
              value={newWsName}
              onChange={(e) => setNewWsName(e.target.value)}
              placeholder="Tên workspace..."
              className="new-ws-input"
            />
            <button type="submit" className="new-ws-submit" disabled={creating}>
              {creating ? "..." : "Tạo"}
            </button>
          </form>
        )}

        <nav className="ws-list">
          {loading ? (
            <div className="ws-skeleton">
              {[1,2].map(i => <div key={i} className="skeleton-item"/>)}
            </div>
          ) : workspaces.length === 0 ? (
            <p className="ws-empty">Chưa có workspace nào.<br/>Tạo mới để bắt đầu!</p>
          ) : (
            workspaces.map((ws) => (
              <div
                key={ws.id}
                className={`ws-item ${activeWorkspace === ws.id ? "active" : ""}`}
                onClick={() => onSelectWorkspace(ws.id)}
                onMouseEnter={() => setHoveredWs(ws.id)}
                onMouseLeave={() => setHoveredWs(null)}
              >
                <span className="ws-avatar">
                  {ws.name.charAt(0).toUpperCase()}
                </span>
                <span className="ws-name">{ws.name}</span>

                {/* Nút xóa — chỉ hiện khi hover và là owner */}
                {hoveredWs === ws.id && ws.ownerId === user.uid && (
                  <button
                    className="ws-delete-btn"
                    onClick={(e) => handleDeleteWorkspace(e, ws)}
                    title="Xóa workspace"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                )}

                {activeWorkspace === ws.id && hoveredWs !== ws.id && (
                  <span className="ws-active-dot"/>
                )}
              </div>
            ))
          )}
        </nav>
      </div>

      {/* User */}
      <div className="sidebar-footer">
        <div className="user-info">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="" className="user-avatar"/>
          ) : (
            <div className="user-avatar-fallback">
              {user?.displayName?.charAt(0)}
            </div>
          )}
          <div className="user-meta">
            <span className="user-name">{user?.displayName}</span>
            <span className="user-email">{user?.email}</span>
          </div>
        </div>
        <button className="logout-btn" onClick={handleLogout} title="Đăng xuất">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </aside>
  );
}
